import React from 'react';
import { COLORS, Panel, StatTile, Table } from '../ui';
import { Rag } from './WoDrawer';

/**
 * Screen 1 -- the 8:00 stand-up.
 *
 * Read standing up, so it goes: the three numbers that decide the day, the shape
 * of the open book, where work is sitting, then the ten jobs to talk about.
 * Everything is clickable through to the list behind it.
 */

const pctText = v => v == null ? '—' : `${Math.round(v * 100)}%`;
const daysText = v => v == null ? '—' : `${v.toFixed(1)}d`;

/**
 * Movement since the baseline. Absent is not zero: until a morning has been
 * recorded there is nothing to compare with, and saying "0" would claim a
 * steady day that nobody measured.
 */
function deltaText(delta, label) {
    if (delta == null) return label;
    if (delta === 0) return `no change ${label}`;
    return `${delta > 0 ? '+' : ''}${delta} ${label}`;
}

function Tile({ tile, baselineLabel, tone, sub, onClick }) {
    return (
        <button onClick={onClick} disabled={!onClick} style={{
            border: 'none', background: 'none', padding: 0,
            cursor: onClick ? 'pointer' : 'default',
            textAlign: 'left', flex: '1 1 210px',
        }}>
            <StatTile label={tile.label} value={tile.value} tone={tone} sub={sub} />
        </button>
    );
}

/** Target is zero for the three daily KPIs, so any number at all is bad. */
const dailyTone = value => value > 0 ? 'bad' : 'good';

const money = v => v == null ? '—'
    : '$' + Math.round(v).toLocaleString('en-AU');

const moneyShort = v => {
    if (v == null) return '—';
    if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
    if (Math.abs(v) >= 1e3) return `$${Math.round(v / 1e3)}k`;
    return `$${Math.round(v)}`;
};

/**
 * CAPEX / OPEX against budget, month to date.
 *
 * The bar shows approved value, not invoiced spend — MC carries an actual cost
 * on about 5% of work orders, so invoiced is reported beside it with its
 * coverage rather than driving the card. The marker is business-time pace: past
 * it means the month is committing faster than it is elapsing.
 */
function BudgetCard({ b, onOpen }) {
    const toneColor = b.tone === 'bad' ? COLORS.critical
        : b.tone === 'warn' ? 'var(--ui-tone-warn-fg, #8a6100)' : b.tone === 'good' ? 'var(--ui-tone-good-fg, #046804)' : COLORS.ink2;

    const pct = (v) => b.budget ? Math.max(0, Math.min(100, (v / b.budget) * 100)) : 0;
    const committedPct = pct(b.committed);
    const outstandingPct = Math.min(100 - committedPct, pct(b.outstanding));

    return (
        <Panel
            title={`${b.label} · month to date`}
            subtitle={b.basis}
            right={b.variancePts == null
                ? <span style={{ fontSize: 11.5, color: COLORS.muted }}>budget not configured</span>
                : <span style={{ fontSize: 12, fontWeight: 600, color: toneColor }}>
                    {b.variancePts > 0 ? '+' : ''}{Math.round(b.variancePts)} pts vs pace
                </span>}
        >
            <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.1 }}>
                {money(b.committed)}
                {b.budget && <small style={{ fontSize: 14, fontWeight: 400, color: COLORS.muted }}> / {moneyShort(b.budget)}</small>}
            </div>

            {b.budget ? (
                <div style={{
                    position: 'relative', height: 10, borderRadius: 5, marginTop: 10,
                    background: COLORS.grid, overflow: 'hidden',
                }}>
                    <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${committedPct}%`, background: COLORS.series[0] }} />
                    <span style={{ position: 'absolute', left: `${committedPct}%`, top: 0, bottom: 0, width: `${outstandingPct}%`, background: COLORS.series[0], opacity: 0.35 }} />
                    {b.pace != null && (
                        <span style={{
                            position: 'absolute', left: `${Math.min(100, b.pace * 100)}%`, top: -2, bottom: -2,
                            width: 2, background: COLORS.ink,
                        }} />
                    )}
                </div>
            ) : (
                <div style={{ fontSize: 11.5, color: COLORS.muted, marginTop: 8 }}>
                    Set a monthly budget to show variance against pace.
                </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12, color: COLORS.ink2, marginTop: 10 }}>
                <span>Outstanding on open work {money(b.outstanding)}</span>
                {b.forecast != null && <span>Forecast {moneyShort(b.forecast)}</span>}
            </div>

            <div style={{ fontSize: 11.5, color: COLORS.muted, marginTop: 8 }}>
                {b.workOrders} work orders · business-time pace {b.pace == null ? '—' : `${Math.round(b.pace * 100)}%`}
                {b.budget && ' · solid committed, faded still open, marker pace'}
            </div>

            <div style={{ fontSize: 11.5, color: COLORS.muted, marginTop: 6 }}>
                Invoiced so far {money(b.invoiced)} — MC holds an actual cost on only{' '}
                {b.invoiceCoveragePct == null ? '—' : `${Math.round(b.invoiceCoveragePct * 100)}%`}{' '}
                of these, so it is not used as spend.
            </div>

            <button onClick={onOpen} style={{
                marginTop: 10, background: 'none', border: `1px solid ${COLORS.link}`,
                color: COLORS.link, padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12,
            }}>See the work orders</button>
        </Panel>
    );
}

/**
 * Open work by contract priority.
 *
 * Bands with no open work are not shown -- six chips reading zero say less than
 * the four holding work. P5 and P6 appear for non-pump work too, despite the
 * brief describing them as GVR-only.
 */
function PriorityStrip({ priorities, onPick }) {
    if (!priorities || !priorities.length) return null;
    return (
        <Panel title="Open by priority" subtitle="Contract priority band — click for the list">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {priorities.map(p => (
                    <button
                        key={p.band}
                        onClick={() => onPick(p)}
                        title={`${p.band}: ${p.pumps} pumps · ${p.nonPumps} non-pumps · ${p.breached} breached`}
                        style={{
                            display: 'flex', alignItems: 'baseline', gap: 8, cursor: 'pointer',
                            border: `1px solid ${p.breached ? COLORS.critical : COLORS.axis}`,
                            background: COLORS.surface, borderRadius: 20, padding: '6px 14px',
                        }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.ink2 }}>{p.band}</span>
                        <span style={{ fontSize: 18, fontWeight: 700 }}>{p.count}</span>
                        {p.breached > 0 && (
                            <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.critical }}>
                                {p.breached} breached
                            </span>
                        )}
                    </button>
                ))}
            </div>
        </Panel>
    );
}

/**
 * The stage strip: four segments sized by how much open work sits in each.
 *
 * This is the "where is work sitting" view. Widths are proportional to count,
 * with a floor so a stage holding a handful of jobs is still clickable.
 */
function StageStrip({ stages, onPick }) {
    const total = stages.reduce((sum, s) => sum + s.count, 0) || 1;

    return (
        <div>
            <div style={{ display: 'flex', gap: 3, marginBottom: 10 }}>
                {stages.map(s => {
                    const share = s.count / total;
                    const bad = s.overTarget > 0;
                    return (
                        <button
                            key={s.key}
                            onClick={() => onPick(s)}
                            title={`${s.label}: ${s.count} open, ${s.overTarget} over target`}
                            style={{
                                flex: `${Math.max(share, 0.08)} 1 0`,
                                minWidth: 92, cursor: 'pointer', textAlign: 'left',
                                border: `1px solid ${bad ? COLORS.critical : COLORS.grid}`,
                                borderLeft: `4px solid ${bad ? COLORS.critical : COLORS.good}`,
                                background: COLORS.surface, borderRadius: 6, padding: '10px 12px',
                            }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.ink2, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                                {s.label}
                            </div>
                            <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>{s.count}</div>
                            <div style={{ fontSize: 11.5, color: COLORS.muted }}>
                                median {daysText(s.medianBusinessDays)}
                                {s.targetBusinessDays != null && ` · target ${s.targetBusinessDays}d`}
                            </div>
                            <div style={{ fontSize: 11.5, color: bad ? COLORS.critical : COLORS.muted, fontWeight: bad ? 600 : 400 }}>
                                {s.overTarget} over target
                            </div>
                        </button>
                    );
                })}
            </div>
            <div style={{ fontSize: 11.5, color: COLORS.muted }}>
                Owners: dwell and close-out sit with the coordinators, approval with the approver,
                rectification with the contractor. Stage medians are separate measures — they do not
                sum to the end-to-end cycle time.
            </div>
        </div>
    );
}

export default function StandupScreen({ data, onOpenList }) {
    const { tiles, priorities, budgets, stages, daily10, dormant, baseline } = data;
    const since = baseline?.label || 'no baseline yet';

    const pumpColumns = [
        { key: 'rag', label: '', render: r => <Rag value={r.rag} /> },
        { key: 'id', label: 'WO', render: r => <span style={{ fontWeight: 600 }}>{r.id}</span> },
        { key: 'site', label: 'Site', render: r => `${r.site || ''} (${r.state || ''})` },
        { key: 'priority', label: 'Priority', render: r => (r.priority || '').replace(/^(GVR|PSA) /, '') },
        {
            key: 'slaPct', label: 'SLA used', align: 'right',
            render: r => <span style={{ fontWeight: 600, color: r.rag === 'red' ? COLORS.critical : COLORS.ink }}>
                {pctText(r.slaPct)}
            </span>,
        },
        { key: 'contractor', label: 'Contractor', render: r => (r.contractor || '—').slice(0, 22) },
        { key: 'escalation', label: 'Next step' },
    ];

    const nonPumpColumns = [
        { key: 'rag', label: '', render: r => <Rag value={r.rag} /> },
        { key: 'id', label: 'WO', render: r => <span style={{ fontWeight: 600 }}>{r.id}</span> },
        { key: 'site', label: 'Site', render: r => `${r.site || ''} (${r.state || ''})` },
        { key: 'ageDays', label: 'Age', align: 'right', render: r => `${Math.round(r.ageDays)}d` },
        { key: 'contractor', label: 'Contractor', render: r => (r.contractor || '—').slice(0, 22) },
        { key: 'person', label: 'Owner', render: r => r.person || '—' },
        { key: 'escalation', label: 'Next step' },
    ];

    const streamSub = (tiles.openWork?.streams || [])
        .map(s => `${s.stream}: ${s.count}${s.breached ? ` / ${s.breached} breached` : ''}`)
        .join(' · ');

    return (
        <>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
                <Tile tile={tiles.d1} tone={dailyTone(tiles.d1.value)}
                    sub={`${deltaText(tiles.d1.delta, since)} · ${tiles.d1.hint}`}
                    onClick={() => onOpenList('waiting', tiles.d1.label)} />
                <Tile tile={tiles.d2} tone={dailyTone(tiles.d2.value)}
                    sub={`${deltaText(tiles.d2.delta, since)} · ${tiles.d2.hint}`}
                    onClick={() => onOpenList('stale', tiles.d2.label)} />
                <Tile tile={tiles.d3} tone={dailyTone(tiles.d3.value)}
                    sub={`${deltaText(tiles.d3.delta, since)} · ${tiles.d3.hint}`}
                    onClick={() => onOpenList('breached', tiles.d3.label)} />
                <Tile tile={tiles.openWork}
                    sub={`${deltaText(tiles.openWork.delta, since)}${streamSub ? ` · ${streamSub}` : ''}`}
                    onClick={() => onOpenList('active', 'Open work orders')} />
                <Tile tile={tiles.newSince}
                    tone={tiles.newSince.value > tiles.newSince.previous ? 'warn' : undefined}
                    sub={`${tiles.newSince.previous} in the previous period · ${tiles.newSince.unissued} unissued · ${tiles.newSince.noOwner} no contractor`}
                    onClick={() => onOpenList('opened', 'Raised in this period')} />
            </div>

            <PriorityStrip
                priorities={priorities}
                onPick={p => onOpenList('priority', `Open · ${p.band} (${p.count})`, { band: p.band })}
            />

            {budgets && budgets.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 16 }}>
                    {budgets.map(b => (
                        <BudgetCard key={b.bucket} b={b}
                            onOpen={() => onOpenList('bucket', `${b.label} · this month`, { bucket: b.bucket })} />
                    ))}
                </div>
            )}

            <Panel title="Where the work is sitting" subtitle="Open work orders by stage, with median time in stage">
                <StageStrip stages={stages} onPick={s => onOpenList('stage', `${s.label} — ${s.count} open`, { stage: s.key })} />
            </Panel>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(440px, 1fr))', gap: 16 }}>
                <Panel title="The Daily 10 — pumps" subtitle="Most of the contract SLA consumed">
                    <Table columns={pumpColumns} rows={daily10.pumps} rowKey={r => r.pk}
                        maxHeight={260} empty="No pump work orders in scope." />
                </Panel>

                <Panel title="The Daily 10 — non-pumps" subtitle="Oldest open work · amber from 10 days, breach at 14">
                    <Table columns={nonPumpColumns} rows={daily10.nonPumps} rowKey={r => r.pk}
                        maxHeight={260} empty="No non-pump work orders in scope." />
                </Panel>
            </div>

            {dormant && dormant.value > 0 && (
                <Panel title="Not on the ladder" subtitle="Reported so the pile cannot quietly grow">
                    <button onClick={() => onOpenList('dormant', dormant.label)}
                        style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}>
                        <StatTile label="Dormant" value={dormant.value} sub="open, untouched 12+ months" />
                    </button>
                    <div style={{ fontSize: 11.5, color: COLORS.muted, marginTop: 10 }}>{dormant.note}</div>
                </Panel>
            )}
        </>
    );
}
